import { SITE_INSIDE, SITE_PLACE, SITE_STOP, type SiteInside, type SitePlace, type SiteStop } from "../../convex/constants/siteAccess.lib";
import type { SiteAccess } from "../../convex/schemas/validators/siteAccess.lib";
import type { FreightVehicle } from "../../convex/schemas/validators/freightShipment.lib";
import { WARP_DELIVERY_SERVICE, WARP_PICKUP_SERVICE } from "../../convex/constants/warpFreight.lib";

export type WarpPickupServiceOption = (typeof WARP_PICKUP_SERVICE)[keyof typeof WARP_PICKUP_SERVICE];
export type WarpDeliveryServiceOption = (typeof WARP_DELIVERY_SERVICE)[keyof typeof WARP_DELIVERY_SERVICE];
export type WarpServiceOption = WarpPickupServiceOption | WarpDeliveryServiceOption;

type StopCodes = typeof WARP_PICKUP_SERVICE | typeof WARP_DELIVERY_SERVICE;

const PLACE_CODE: Record<SitePlace, keyof StopCodes | undefined> = {
  [SITE_PLACE.BUSINESS]: undefined,
  [SITE_PLACE.RESIDENCE]: "RESIDENTIAL",
  [SITE_PLACE.CONSTRUCTION_SITE]: "CONSTRUCTION_SITE",
  [SITE_PLACE.TRADE_SHOW]: "TRADE_SHOW",
  [SITE_PLACE.LIMITED_ACCESS]: "LIMITED_ACCESS",
};

const INSIDE_CODE: Record<SiteInside, keyof StopCodes | undefined> = {
  [SITE_INSIDE.NONE]: undefined,
  [SITE_INSIDE.GROUND_FLOOR]: "INSIDE",
  [SITE_INSIDE.UPSTAIRS]: "INSIDE_UPSTAIRS",
  [SITE_INSIDE.BACK_OF_HOUSE]: "INSIDE_BACK_OF_HOUSE",
  [SITE_INSIDE.LIVING_SPACE]: "INSIDE_LIVING_SPACE",
};

export function needsLiftgate(access: SiteAccess, vehicle?: FreightVehicle): boolean {
  if (access.dock === true) return false;
  return !(vehicle === "flatbed" && access.forklift === true);
}

function stopCodes(stop: SiteStop): StopCodes {
  return stop === SITE_STOP.PICKUP ? WARP_PICKUP_SERVICE : WARP_DELIVERY_SERVICE;
}

export function siteAccessServiceOptions(access: SiteAccess | undefined, stop: typeof SITE_STOP.PICKUP): WarpPickupServiceOption[];
export function siteAccessServiceOptions(access: SiteAccess | undefined, stop: typeof SITE_STOP.DELIVERY): WarpDeliveryServiceOption[];
export function siteAccessServiceOptions(access: SiteAccess | undefined, stop: SiteStop): WarpServiceOption[];
export function siteAccessServiceOptions(access: SiteAccess | undefined, stop: SiteStop): WarpServiceOption[] {
  if (!access) return [];
  const codes = stopCodes(stop);
  const keys: Array<keyof StopCodes | undefined> = [
    access.place ? PLACE_CODE[access.place] : undefined,
    access.inside ? INSIDE_CODE[access.inside] : undefined,
    needsLiftgate(access) ? "LIFTGATE" : undefined,
    access.appointment ? "APPOINTMENT" : undefined,
    access.driverAssist ? "DRIVER_ASSIST" : undefined,
    access.dropTrailer ? "DROP_AND_HOOK" : undefined,
  ];
  const options: WarpServiceOption[] = keys.flatMap((key) => (key ? [codes[key]] : []));
  if (stop === SITE_STOP.DELIVERY && access.lumper) options.push(WARP_DELIVERY_SERVICE.LUMPER);
  return [...new Set(options)].sort();
}
